import { useState, useCallback, useEffect } from 'react';
import { DsnListItem } from '../pages/dsn-list/types/dsntable.types';

interface TableRowHandlingProps {
  data: DsnListItem[];
  onPaginationChange: (page: number, pageSize: number) => void;
  pageSize: number;
  initialActiveRowId?: string;
  onSelectedRowsChange?: (selectedRows: Record<string, boolean>) => void;
}

export const useTableRowHandling = ({
  data,
  onPaginationChange,
  pageSize,
  initialActiveRowId,
  onSelectedRowsChange,
}: TableRowHandlingProps) => {
  const [selectAll, setSelectAll] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});
  const [highlightedRow, setHighlightedRow] = useState('');
  const [activeRow, setActiveRow] = useState(() =>
    initialActiveRowId && data.some(row => row.InstanceId === initialActiveRowId)
      ? initialActiveRowId
      : '',
  );

  useEffect(() => {
    setActiveRow(currentActiveRow =>
      currentActiveRow && !data.some(row => row.InstanceId === currentActiveRow)
        ? ''
        : currentActiveRow,
    );
  }, [data]);

  const handleSelectRow = useCallback(
    (id: string) => {
      setSelectedRows(prevRows => {
        const newRows = {
          ...prevRows,
          [id]: !prevRows[id],
        };

        if (onSelectedRowsChange) {
          onSelectedRowsChange(newRows);
        }

        return newRows;
      });
    },
    [onSelectedRowsChange],
  );

  const handleSelectAllRows = useCallback(() => {
    const newSelectedState = !selectAll;

    const newSelectedRows: Record<string, boolean> = {};

    data?.forEach(row => {
      newSelectedRows[row.InstanceId] = newSelectedState;
    });

    if (onSelectedRowsChange) {
      onSelectedRowsChange(newSelectedRows);
    }

    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedState);
  }, [data, selectAll, onSelectedRowsChange]);

  const handlePaginationChange = useCallback(
    (page: number, newPageSize?: number) => {
      onPaginationChange(page, newPageSize ?? pageSize);
    },
    [onPaginationChange, pageSize],
  );

  const handleHighlightRow = (id: string) => {
    setHighlightedRow(id === highlightedRow ? '' : id);
  };

  return {
    selectAll,
    selectedRows,
    highlightedRow,
    setHighlightedRow,
    activeRow,
    setActiveRow,
    handleSelectRow,
    handleSelectAllRows,
    handlePaginationChange,
    handleHighlightRow,
  };
};
